"use client";

import { useEffect, useState, useRef } from "react";
import { useRouter } from "next/navigation";
import Lock from "@mui/icons-material/Lock";
import { X } from "lucide-react";

import { useStyles } from "./styles";
import { MODELS } from "../constants";

const ASPECT_RATIOS = [
  { ratio: "1:1", x: 8, y: 8, width: 16, height: 16, premiumOnly: false },
  { ratio: "2:3", x: 11, y: 8.5, width: 10, height: 15, premiumOnly: false },
  { ratio: "9:16", x: 12, y: 4, width: 8, height: 24, premiumOnly: true },
  { ratio: "16:9", x: 4, y: 12, width: 24, height: 8, premiumOnly: true },
  { ratio: "3:4", x: 10, y: 6, width: 12, height: 20, premiumOnly: true },
  { ratio: "4:3", x: 6, y: 10, width: 20, height: 12, premiumOnly: true },
  { ratio: "3:2", x: 5.5, y: 9, width: 21, height: 14, premiumOnly: true },
  { ratio: "21:9", x: 5, y: 13, width: 22, height: 6, premiumOnly: true },
];

interface AspectRatioSelectProps {
  selectedModel: string;
  aspectRatio: string;
  setAspectRatio: (ratio: string) => void;
  isSubscribed: boolean;
}

const AspectRatioSelect: React.FC<AspectRatioSelectProps> = ({
  selectedModel,
  aspectRatio,
  setAspectRatio,
  isSubscribed,
}) => {
  const { classes, cx } = useStyles();
  const router = useRouter();
  const model = MODELS.find((m) => m.name === selectedModel) || MODELS[0];
  const [tooltipInfo, setTooltipInfo] = useState<{
    element: HTMLElement;
    ratio: string;
  } | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!model.includedAspectRatios.includes(aspectRatio)) {
      setAspectRatio("1:1");
    }
  }, [selectedModel]);

  useEffect(() => {
    if (tooltipInfo) {
      const timer = setTimeout(() => {
        setTooltipInfo(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [tooltipInfo]);

  useEffect(() => {
    if (tooltipInfo && tooltipRef.current) {
      const rect = tooltipInfo.element.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();

      const top = rect.top - tooltipRect.height - 12;
      const left = rect.left + rect.width / 2 - tooltipRect.width / 2;

      tooltipRef.current.style.top = `${Math.max(10, top)}px`;
      tooltipRef.current.style.left = `${Math.max(
        10,
        Math.min(left, window.innerWidth - tooltipRect.width - 10)
      )}px`;
    }
  }, [tooltipInfo]);

  const handleClick = (ratio: string) => {
    if (model.includedAspectRatios.includes(ratio)) {
      setAspectRatio(ratio);
    }
  };

  const handleLockedClick = (
    ratio: string,
    event: React.MouseEvent<HTMLDivElement>
  ) => {
    event.stopPropagation();
    setTooltipInfo({ element: event.currentTarget, ratio });
  };

  const handleUpgrade = () => {
    router.push("/subscribe");
    setTooltipInfo(null);
  };

  return (
    <div className={classes.container}>
      <h3 className={classes.heading}>Aspect Ratio</h3>
      <div className={classes.controls}>
        {ASPECT_RATIOS.map(({ ratio, x, y, width, height, premiumOnly }) => {
          const locked = premiumOnly && !isSubscribed;
          const disabled = !model.includedAspectRatios.includes(ratio);

          return (
            <div className={classes.controlButtonContainer} key={ratio}>
              <button
                className={cx(classes.controlButton, {
                  [classes.selected]: aspectRatio === ratio,
                })}
                onClick={() => handleClick(ratio)}
                disabled={disabled || locked}
              >
                <svg
                  viewBox="0 0 32 32"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className={classes.controlButtonIcon}
                >
                  <rect x={x} y={y} width={width} height={height} rx="2" />
                </svg>
                {ratio}
              </button>
              {locked && (
                <>
                  <div className={classes.locked}>
                    <Lock />
                  </div>
                  <div
                    className={classes.clickOverlay}
                    onClick={(e) => handleLockedClick(ratio, e)}
                  />
                </>
              )}
            </div>
          );
        })}
      </div>
      {tooltipInfo && (
        <div ref={tooltipRef} className={classes.tooltip}>
          <button
            className={classes.tooltipClose}
            onClick={() => setTooltipInfo(null)}
          >
            <X size={14} />
          </button>
          <p className={classes.tooltipMessage}>
            To use {tooltipInfo.ratio} aspect ratio
          </p>
          <button className={classes.tooltipUpgrade} onClick={handleUpgrade}>
            Upgrade
          </button>
        </div>
      )}
    </div>
  );
};

export default AspectRatioSelect;
